// Global imperative notify / confirm — replaces window.alert and
// window.confirm with our own modal. The browser-native dialogs show
// the parent window's title at the top ("Claude" inside the preview,
// random URL bits in some browsers, nothing in others) and they're
// styled inconsistently across OSes — this module renders a normal
// React modal we control.
//
// Usage from anywhere:
//   import { notify, confirmDialog } from '../notify';
//   notify('Saved successfully');
//   if (!(await confirmDialog('Delete this row?'))) return;
//
// The actual modal is rendered once in App.jsx via <NotifyHost /> —
// which calls _register on mount and _unregister on unmount, keeping
// the imperative dispatcher pointed at the live React state setter.

let _push = null;

export function _register(pushFn) { _push = pushFn; }
export function _unregister() { _push = null; }

// Fire-and-forget. Replaces alert(...).
export function notify(message, opts = {}) {
  // Title defaults to a soft "Notice" — callers can override via opts.title
  // ('Saved', 'Error', etc). Variant ('info' | 'success' | 'error') colors
  // the header.
  if (!_push) {
    // Until the host mounts (very first render), fall back to console
    // so messages aren't silently swallowed. Should never fire after
    // App finishes mounting.
    console.warn('[notify]', message);
    return;
  }
  _push({ kind: 'alert', title: opts.title || 'Notice', message: String(message ?? ''), variant: opts.variant || 'info' });
}

// Returns a Promise<string|null> — the typed text, or null if cancelled.
// Replaces window.prompt(...), whose native dialog shows the bare hostname
// ("hr.zucol.in says") and ignores the app's styling entirely.
export function promptDialog(message, opts = {}) {
  return new Promise((resolve) => {
    if (!_push) { resolve(null); return; }
    _push({
      kind: 'prompt',
      title: opts.title || 'Enter a value',
      message: String(message ?? ''),
      defaultValue: opts.defaultValue || '',
      placeholder: opts.placeholder || '',
      multiline: !!opts.multiline,
      required: opts.required !== false,     // most callers need a value
      inputType: opts.inputType || 'text',
      okLabel: opts.okLabel || 'OK',
      cancelLabel: opts.cancelLabel || 'Cancel',
      variant: opts.variant || 'info',
      resolve,
    });
  });
}

// Returns a Promise<boolean>. Replaces if (!window.confirm(...)).
// Caller MUST await — that's the trade-off for custom UI.
export function confirmDialog(message, opts = {}) {
  return new Promise((resolve) => {
    if (!_push) { resolve(false); return; }
    _push({
      kind: 'confirm',
      title: opts.title || 'Confirm',
      message: String(message ?? ''),
      okLabel: opts.okLabel || 'OK',
      cancelLabel: opts.cancelLabel || 'Cancel',
      variant: opts.variant || 'warn', // 'warn' | 'danger'
      resolve,
    });
  });
}
